<template>
    <base-dialog :loading="loading" :value="dialog" :width="780" :fullscreen="isMobile" persistent
        :title="editMode ? $t('common.edit_circuit') : $t('common.create_circuit')"
        @input="val => { if (!val) hide() }" @close="hide">

        <skeleton-loader v-if="loadingEditMode" type="rows" :count="4" />
        <div v-else class="cc-body">
            <category-icon-picker :items="categoriesOptions" :value="category" :loading="loadingCategories"
                @change="onClickCategory" />

            <div v-if="!category" class="cc-hint">{{ $t('exercises.need_select_an_category') }}</div>

            <template v-else>
                <tabs-group :value="currentCircuitType" color="grey" :tabs="circuitTypes"
                    @change="currentCircuitType = $event" />

                <kimsa-text-field v-if="!calendarMode" :label="$t('circuits.name')" dense force-value :value="name"
                    @change="name = $event" />

                <div class="cc-grid">
                    <kimsa-combobox :label="$t('exercises.types')" :placeholder="$t('exercises.pick_type')" dense
                        :items="typesOptions" item-text="label" @change="types = $event" placeholder-class
                        multiple-with-chips :readonly="!category" :value="types" ref="types" />
                    <kimsa-combobox can-write @create="showCreateExercise" :label="$tc('exercises.exercises', 1)"
                        :placeholder="$t('exercises.pick_exercise')" dense :items="exercisesOptions"
                        @change="onExerciseChange" item-value="objectID" placeholder-class ref="exercise" show-video
                        @search="onSearchExercises" :no-create-exercise="isFreePlan" @showVideo="onShowVideo" />
                    <kimsa-combobox :label="labelSets" :value="sets" dense :items="setOptions" @change="sets = $event"
                        placeholder-class ref="sets" />
                </div>

                <div v-if="currentCircuitType === 'tabata'" class="cc-grid cc-grid--tabata">
                    <kimsa-combobox :label="$t('common.laps')" :value="minutes" dense :items="minutesOptions"
                        @change="minutes = $event" placeholder-class ref="minutes" />
                    <kimsa-combobox :label="$t('common.time_work')" :value="timeWork" dense :items="secondOptions"
                        @change="timeWork = $event" placeholder-class ref="timeWork" />
                    <kimsa-combobox :label="$t('common.time_rest')" :value="timeRest" dense :items="secondOptions"
                        @change="timeRest = $event" placeholder-class ref="timeRest" />
                    <kimsa-combobox v-if="sets > 1" :label="$t('common.time_pause')" :value="timePause" dense
                        :items="secondOptions" @change="timePause = $event" placeholder-class ref="timeRestPause" />
                </div>

                <div class="cc-ex">
                    <div class="cc-ex-head">{{ $tc('common.exercises', 2) }}</div>
                    <div v-if="exercises.length === 0" class="cc-ex-empty">{{ $t('common.add_exercises_for_config') }}
                    </div>
                    <div v-for="(exercise, i) in exercises" :key="exerciseKey(exercise) || i" class="cc-ex-row">
                        <div class="cc-ex-name">{{ exercise.name }}</div>
                        <kimsa-combobox v-if="currentCircuitType !== 'tabata'" class="cc-ex-type"
                            :placeholder="$t('common.select_placeholder')" dense :items="repsOptions"
                            @change="onChangeRepsType($event, i)" :value="exercise.config.type" placeholder-class
                            :icon="setTypeIcon(exercise.config.type)" />
                        <v-text-field v-if="currentCircuitType !== 'tabata'" class="cc-ex-input" solo flat dense
                            hide-details :placeholder="$t('common.value')" @keypress="onRepsKeypress"
                            :readonly="!exercise.config.type" v-model="exercise.config.value" />
                        <v-text-field class="cc-ex-input cc-ex-note" solo flat dense hide-details
                            :placeholder="$t('common.note')" v-model="exercise.config.note" maxlength="6" />
                        <button type="button" class="cc-ex-del" @click="deleteExercise(i)">
                            <v-icon size="14">$delete</v-icon>
                        </button>
                    </div>
                </div>
            </template>
        </div>

        <template v-slot:footer>
            <dialog-actions hide-cancel :confirm-label="editMode ? $t('common.save') : $t('common.create')"
                :disabled="disabledAdd" :loading="loading" @confirm="add" />
        </template>

        <exercise-creator-desktop-dialog is-second-dialog ref="creator" @success="onSuccessCreate" />
        <watch-video-dialog @hide="onHideVideo" :thumb="currentImg" :video="currentVideo" is-second-dialog
            ref="watchVideo" />
    </base-dialog>
</template>

<script>
import { mapActions, mapState } from "vuex";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import { searchExercises } from '@/utils/ExerciseLibrary'
import { clearBlock, exerciseKey, setTypeIcon, getRepsOptions, validateRepsKey } from "@/utils/Exercises";
import { db, Timestamp } from "@/utils/Firebase";
import KimsaTextField from "@/components/common/inputs/KimsaTextField";
import ExerciseCreatorDesktopDialog from "@/components/coach/routines/ExerciseCreatorDesktopDialog";
import WatchVideoDialog from "@/components/common/routines/WatchVideoDialog";
import TabsGroup from '../../common/TabsGroup.vue';
import BaseDialog from "@/components/common/BaseDialog";
import DialogActions from "@/components/common/DialogActions";
import CategoryIconPicker from "@/components/common/inputs/CategoryIconPicker";
import SkeletonLoader from "@/components/common/SkeletonLoader";
import { debounce } from "@/utils/Common";

export default {
    name: "CreateCircuitDialog",
    props: {
        coachId: {
            type: String,
            default: null,
        },
        calendarMode: {
            type: Boolean,
            default: false,
        },
        collection: {
            type: String,
            default: 'assignedRoutines',
        },
        noSave: {
            type: Boolean,
            default: false,
        },
    },
    components: {
        WatchVideoDialog,
        ExerciseCreatorDesktopDialog,
        KimsaTextField,
        KimsaCombobox,
        TabsGroup,
        BaseDialog,
        DialogActions,
        CategoryIconPicker,
        SkeletonLoader,
    },
    data(vm) {
        return {
            dialog: false,
            loading: false,
            editMode: false,
            loadingEditMode: false,
            circuit: null,
            circuitTypes: [
                { label: vm.$t('circuits.emom'), value: 'emom' },
                { label: vm.$t('circuits.amrap'), value: 'amrap' },
                { label: vm.$t('circuits.time'), value: 'time' },
                { label: vm.$t('circuits.tabata'), value: 'tabata' },
            ],
            name: '',
            currentCircuitType: 'emom',
            category: null,
            types: [],
            exercisesOptions: [],
            exercises: [],
            sets: 1,
            minutes: 1,
            timeWork: 1,
            timeRest: 1,
            timePause: 1,
            currentImg: null,
            currentVideo: null,
            search: ''
        }
    },
    computed: {
        ...mapState(['categoriesOptions', "loadingCategories", 'globalFolders']),
        labelSets(vm = this) {
            if (vm.currentCircuitType === 'emom' || vm.currentCircuitType === 'amrap') return vm.$tc('common.time')
            if (vm.currentCircuitType === 'time') return vm.$tc('common.laps')
            return vm.$tc('exercises.sets', 2)
        },
        setOptions(vm = this) {
            let quantity = 10
            if (vm.currentCircuitType === 'emom') quantity = 15
            if (vm.currentCircuitType === 'amrap') quantity = 25
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        minutesOptions(vm = this) {
            let quantity = 10
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        secondOptions(vm = this) {
            let quantity = 60
            return Array.from({ length: quantity }, (_, i) => i + 1);
        },
        repsOptions(vm = this) {
            return getRepsOptions(vm.hasIntensity)
        },
        typesOptions(vm = this) {
            if (!vm.category) return []
            return vm.category.types.map(type => ({ name: type, value: type, label: vm.typeLabel(type) })).sort((a, b) => a.label.localeCompare(b.label))
        },
        hasIntensity(vm = this) {
            return vm.categoryKey(vm.category?.name) === vm.CATEGORY.SPEED || vm.categoryKey(vm.category?.name) === vm.CATEGORY.RESISTANCE
        },
        disabledAdd(vm = this) {
            if (vm.calendarMode) return !vm.category || !vm.sets || vm.exercises.length === 0 || vm.exercises.some(ex => vm.currentCircuitType !== 'tabata' && (!ex.config.type || !ex.config.value))
            return !vm.name || !vm.category || !vm.sets || vm.exercises.length === 0 || vm.exercises.some(ex => vm.currentCircuitType !== 'tabata' && (!ex.config.type || !ex.config.value))
        },
        circuitFolder(vm = this) {
            return vm.globalFolders?.find(gf => gf.isCircuit) || null
        },
        circuitId(vm = this) {
            return vm.circuit?.id || vm.circuit?.objectID
        }
    },
    async mounted() {
        const vm = this
        if (vm.categoriesOptions.length === 0) await vm.fetchExerciseCategories()
        if (vm.globalFolders.length === 0) await vm.bindGlobalFolders()
    },
    methods: {
        exerciseKey,
        setTypeIcon,
        ...mapActions(['fetchExerciseCategories', 'bindGlobalFolders']),
        show() {
            const vm = this
            vm.dialog = true
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.reset()
        },
        reset() {
            const vm = this
            vm.editMode = false
            vm.name = ''
            vm.category = null
            vm.types = []
            vm.sets = 1
            vm.exercises = []
        },

        async setEditingMode(circuit) {
            const vm = this
            vm.editMode = true
            vm.loadingEditMode = true
            vm.show()
            vm.circuit = circuit

            vm.currentCircuitType = vm.circuit.type
            vm.name = vm.circuit.name
            vm.exercises = vm.circuit.block.exercises
            vm.category = vm.categoriesOptions.find(category => category.name === vm.exercises[0].category)
            vm.types = []
            vm.sets = vm.circuit.sets || 1
            vm.minutes = vm.circuit.block.tabata?.minutes || 1
            vm.timeWork = vm.circuit.block.tabata?.timeWork || 1
            vm.timeRest = vm.circuit.block.tabata?.timeRest || 1
            vm.timePause = vm.circuit.block.tabata?.timePause || 1

            vm.loadingEditMode = false
        },
        async setEditingModeFromCalendar(circuit, routine) {
            const vm = this
            const currentBlock = routine?.blocks?.[circuit.block - 1]
            if (!currentBlock?.exercises?.length) return
            vm.editMode = true
            vm.show()
            await vm.withLoading(async () => {
            vm.circuit = {
                ...currentBlock,
                blocks: routine.blocks,
                block: circuit.block - 1,
                assignedRoutineId: routine.id,
                routineId: routine.routineId || null,
                date: routine.rawDate.full
            }

            const exerciseRef = await db.collection('exercises').doc(circuit.id).get()
            const exerciseCategory = exerciseRef.data().categories?.[0] || null

            vm.currentCircuitType = vm.circuit.type
            vm.name = vm.circuit.name
            vm.exercises = vm.circuit.exercises.map(ex => ({ ...vm.generateExercise(ex) }))
            vm.category = vm.categoriesOptions.find(category => category.name === exerciseCategory)
            vm.types = []
            vm.sets = vm.circuit.sets || 1
            vm.minutes = vm.circuit.tabata?.minutes || 1
            vm.timeWork = vm.circuit.tabata?.timeWork || 1
            vm.timeRest = vm.circuit.tabata?.timeRest || 1
            vm.timePause = vm.circuit.tabata?.timePause || 1
            }, 'loadingEditMode')
        },

        // CATEGORY
        onClickCategory(category) {
            const vm = this
            vm.category = category
        },
        selectColorCategory(category) {
            return this.category?.name === category.name
        },

        // EXERCISES
        async loadExercises() {
            let vm = this
            if (!vm.account) return
            vm.exercisesOptions = []

            const companyId = (vm.account.accountType === 'coach_company' || vm.isCompany)
                ? (vm.account?.coachSubscription?.companyId || vm.currentUser.uid)
                : null

            vm.exercisesOptions = await searchExercises({
                coachId: vm.coachId,
                companyId,
                query: vm.search,
                types: vm.types,
            })
        },
        onSearchExercises(search) {
            const vm = this;
            vm.search = search || ''
            vm.debouncedLoadExercises()
        },
        debouncedLoadExercises: debounce(
            function () {
                this.loadExercises()
            }, 300),
        showCreateExercise(exercise) {
            const vm = this
            let payload = {
                name: exercise,
                types: vm.types?.map(type => type.name) || [],
                categories: [vm.category.name]
            }
            vm.$refs.creator.show(payload)
        },
        async onSuccessCreate(exercise) {
            const vm = this
            vm.exercisesOptions.unshift(exercise)
            vm.onExerciseChange(exercise)
        },
        async onShowVideo(item) {
            const vm = this
            vm.currentImg = item.img || item.video?.thumb || null
            vm.currentVideo = item.video?.url || item?.youtubeUrl || null
            const isYoutubeVideo = item?.youtubeUrl ? true : false
            if (vm.currentVideo) vm.$refs.watchVideo.show(isYoutubeVideo)
        },
        onHideVideo() {
            if (this.$refs.exercise) this.$refs.exercise.openMenu()
        },
        onExerciseChange(exercise) {
            const vm = this;
            if (vm.exercises.find(ex => exerciseKey(ex) === exerciseKey(exercise))) return;
            let payload = vm.generateExercise(exercise)
            vm.exercises.push(payload)
            if (vm.$refs.exercise) {
                vm.$nextTick(() => {
                    vm.$refs.exercise.model = null
                    vm.$refs.exercise.search = null
                })
            }
        },
        generateExercise(exercise) {
            return {
                id: exerciseKey(exercise),
                type: 'exercise',
                name: exercise.name,
                config: exercise.config || { type: null, value: null, note: null },
                category: exercise.categories?.[0] || exercise.category || null
            }
        },
        deleteExercise(index) {
            const vm = this;
            vm.exercises.splice(index, 1)
        },

        // SETS
        onChangeRepsType(repType, index) {
            const vm = this;
            let value = repType?.value || repType
            vm.exercises[index].config.type = value
        },
        // VALIDATIONS
        onRepsKeypress(event) {
            validateRepsKey(event)
        },

        // ADD/EDIT
        async add() {
            const vm = this
            await vm.withLoading(async () => {

            let payload = {
                type: vm.currentCircuitType,
                name: vm.name,
                block: {
                    completed: false,
                    exercises: vm.exercises,
                    sets: vm.sets,
                    isCircuit: true,
                    type: vm.currentCircuitType,
                },
                sets: vm.sets,
                coachId: vm.coachId,
                folder: vm.circuitFolder?.id,
                folderName: vm.circuitFolder?.name,
                isCircuit: true,
            }

            if (vm.currentCircuitType === 'tabata') {
                payload.block.tabata = {
                    minutes: vm.minutes,
                    timeWork: vm.timeWork,
                    timeRest: vm.timeRest,
                    timePause: vm.timePause,
                    sets: vm.sets,
                }

                payload.block.exercises = vm.exercises.map(ex => {
                    ex.config.type = 'time'
                    ex.config.value = vm.timeWork
                    return ex
                })
            }

            if (vm.calendarMode) {
                const newBlock = clearBlock({
                    ...payload.block,
                    completed: vm.circuit.blocks[vm.circuit.block].completed || false
                }, false)

                if (vm.noSave) {
                    vm.$emit('success', { block: newBlock, blockIndex: vm.circuit.block })
                    vm.hide()
                    return
                }

                let blocks = vm.circuit.blocks
                blocks.splice(vm.circuit.block, 1, newBlock).map((b, i) => ({ ...b, id: i + 1 }))

                await db.collection(vm.collection).doc(vm.circuit.assignedRoutineId).update({ blocks })

                const payloadEmitCircuit = {
                    assignedRoutineId: vm.circuit.assignedRoutineId,
                    routineId: vm.circuit.routineId,
                    date: vm.circuit.date
                }
                vm.$emit('success', payloadEmitCircuit)
            } else {
                let objectID = vm.circuitId
                if (vm.editMode) {
                    await db.collection('blocks').doc(vm.circuitId).update(payload)
                } else {
                    payload.created_at = Timestamp.now()
                    const created = await db.collection('blocks').add(payload)
                    objectID = created.id
                }
                vm.$emit('success', { objectID, ...payload })
            }

            vm.toast(vm.$t('circuits.saved'))
            vm.hide()
            })
        },
    },
    watch: {

        category() {
            this.types = []
        },
        types() {
            const vm = this
            vm.loadExercises()
        },
    }
}
</script>

<style scoped>
.cc-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
}

.cc-hint {
    text-align: center;
    font-size: 13px;
    color: var(--v-txtMuted-base);
    padding: 24px 0;
}

.cc-grid {
    display: grid;
    grid-template-columns: 1fr 1fr 110px;
    gap: 12px;
    align-items: start;
}

.cc-grid--tabata {
    grid-template-columns: repeat(4, 1fr);
}

.cc-ex {
    border-top: 1px solid var(--v-outline-base);
    padding-top: 12px;
}

.cc-ex-head {
    font-size: 12px;
    font-weight: 600;
    color: var(--v-txtSecondary-base);
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 10px;
}

.cc-ex-empty {
    text-align: center;
    font-size: 12px;
    color: var(--v-txtMuted-base);
    padding: 12px 0;
}

.cc-ex-row {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
}

.cc-ex-name {
    flex: 2;
    min-width: 0;
    font-size: 13px;
    color: var(--v-txtPrimary-base);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.cc-ex-type {
    flex: 1.4;
    min-width: 0;
}

.cc-ex-input {
    width: 84px;
    flex-shrink: 0;
}

.cc-ex-note {
    flex: 1;
    width: auto;
    min-width: 80px;
}

.cc-ex-del {
    width: 30px;
    height: 30px;
    border-radius: 6px;
    background: rgba(232, 92, 92, .12);
    border: 1px solid rgba(232, 92, 92, .3);
    color: var(--v-error-base);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
}

.cc-ex-del .v-icon {
    color: inherit !important;
}

@media (max-width: 700px) {
    .cc-grid,
    .cc-grid--tabata {
        grid-template-columns: 1fr;
    }
}
</style>